import { useState } from 'react';
import {
	CreateDocumentWorkflowCounterpartyDto,
	DocumentSignatureType,
	DocumentTemplateType,
} from '@dealstack/shared/lib/generated';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';

export const SDWDocumentCharacteristicsController = () => {
	/**************VARS***************** */

	const toastApi = useDSToast();

	const [templateType, setTemplateType] = useState<DocumentTemplateType>(
		DocumentTemplateType.NOTICE
	);

	const [requiredWitnesses, setRequiredWitnesses] = useState<number>(0);

	const [signatureType, setSignatureType] = useState<DocumentSignatureType>(
		DocumentSignatureType.ELECTRONIC
	);

	const [isSignatureAdvanced, setIsSignatureAdvanced] = useState<boolean>(false);
	const [individualScheduleRequired, setIndividualScheduleRequired] = useState<boolean>(false);
	const [isToBeSubmitted, setIsToBeSubmittedState] = useState<boolean>(false);

	/**
	 * "Submit and Sign" = the investor submits the completed form first, then e-signs.
	 * The backend only derives the SIGNABLE_FORM document type (which starts in
	 * PENDING_FORM_SUBMISSION) when this is combined with an ELECTRONIC signature type
	 * — see sdw.mapDocumentToTemplateTypes.ts. Any other signature type would silently
	 * produce a wet-ink FORM_INPUT instead, so we force ELECTRONIC here, matching the
	 * coupling the manager app applies in SendDocumentModal's TemplateSection.
	 */
	const setIsToBeSubmitted = (value: boolean) => {
		setIsToBeSubmittedState(value);
		if (value) {
			setSignatureType(DocumentSignatureType.ELECTRONIC);
		}
	};

	const [emailText, setEmailText] = useState<string | undefined>(undefined);
	const [guidanceNoteFileId, setGuidanceNoteFileId] = useState<string | undefined>(undefined);
	const [counterparties, setCounterparties] = useState<CreateDocumentWorkflowCounterpartyDto[]>(
		[]
	);

	/**************LIFECYCLE***************** */

	/**************METHODS***************** */
	const addCounterparty = (name: string, amount: number) => {
		if (!name || !amount) {
			const message = 'Name and amount are required';
			toastApi.error({ message });
			return;
		}
		if (amount < 1) {
			const message = 'Amount must be greater than 0';
			toastApi.error({ message });
			return;
		}
		if (amount > 6) {
			const message = 'Amount must be less than 6';
			toastApi.error({ message });
			return;
		}
		if (counterparties.find((c) => c.name === name)) {
			const message = 'Counterparty already exists';
			toastApi.error({ message });
			return;
		}
		const counterparty: CreateDocumentWorkflowCounterpartyDto = {
			name,
			amount,
		};
		setCounterparties((prev) => [...prev, counterparty]);
	};

	const removeLastCounterparty = () => {
		if (counterparties.length === 0) {
			return;
		}
		setCounterparties((prev) => prev.slice(0, -1));
	};

	const reset = () => {
		setTemplateType(DocumentTemplateType.NOTICE);
		setRequiredWitnesses(0);
		setSignatureType(DocumentSignatureType.ELECTRONIC);
		setIsSignatureAdvanced(false);
		setIndividualScheduleRequired(false);
		setIsToBeSubmitted(false);
		setEmailText(undefined);
		setGuidanceNoteFileId(undefined);
		setCounterparties([]);
	};

	/**************Return***************** */

	return {
		templateType,
		setTemplateType,
		requiredWitnesses,
		setRequiredWitnesses,
		signatureType,
		setSignatureType,
		isSignatureAdvanced,
		setIsSignatureAdvanced,
		individualScheduleRequired,
		setIndividualScheduleRequired,
		isToBeSubmitted,
		setIsToBeSubmitted,
		emailText,
		setEmailText,
		guidanceNoteFileId,
		setGuidanceNoteFileId,
		counterparties,
		setCounterparties,
		addCounterparty,
		removeLastCounterparty,
		reset,
	};
};
